import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { reportLabels } from '../../constants/reportLabels';
import { useCreateReport, useReportContext } from '../../hooks/useReports';
import { canExport } from '../../utils/reports';
import ExportReportDialog from './ExportReportDialog';

interface ExportReportButtonProps {
  projectId: string;
  /** Edits not saved yet: the report would not reflect them. */
  hasUnsavedChanges: boolean;
}

const ExportReportButton = ({ projectId, hasUnsavedChanges }: ExportReportButtonProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { context, isError, refreshIfOld } = useReportContext(projectId);
  const createReport = useCreateReport(projectId);
  const isGenerating = createReport.isPending;
  const availability = canExport(context, hasUnsavedChanges, isError);
  const isDisabled = !availability.enabled || isGenerating;

  const handleOpen = () => {
    if (!isDisabled) setConfirmOpen(true);
  };

  const handleConfirm = () => {
    createReport.mutate(undefined, { onSettled: () => setConfirmOpen(false) });
  };

  const handleCancel = () => {
    createReport.cancel();
    setConfirmOpen(false);
  };

  return (
    <>
      {/* aria-disabled instead of disabled keeps the button focusable, so keyboard users get the reason too. */}
      <Tooltip title={isGenerating ? reportLabels.generating : availability.reason ?? reportLabels.export_button} describeChild>
        <IconButton
          aria-label={reportLabels.export_button}
          aria-disabled={isDisabled}
          aria-haspopup="dialog"
          onClick={handleOpen}
          onMouseEnter={refreshIfOld}
          onFocus={refreshIfOld}
          loading={isGenerating}
          sx={isDisabled ? { color: 'action.disabled', cursor: 'default' } : undefined}
        >
          <PictureAsPdfIcon />
        </IconButton>
      </Tooltip>
      <ExportReportDialog
        open={confirmOpen}
        packages={context?.packages}
        isGenerating={isGenerating}
        progress={createReport.progress}
        canCancel={createReport.canCancel}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default ExportReportButton;
